import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Timeline as TimelineIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MainLayout } from '../components/MainLayout';
import { chartTooltipProps } from '../components/charts/chartTooltip';
import {
  PerformanceService,
  SUPPORTED_BENCHMARKS,
  type PerformanceResponse,
} from '../services/performance-service';

type WindowPreset = '1M' | '3M' | '6M' | '1Y' | '3Y' | 'All';
const PRESETS: WindowPreset[] = ['1M', '3M', '6M', '1Y', '3Y', 'All'];

/** Window start for a preset, ending today. 'All' reaches back before any
 * plausible ledger history rather than guessing at inception. */
function presetFrom(preset: WindowPreset): string {
  if (preset === 'All') return '1990-01-01';
  const d = new Date();
  if (preset === '1M') d.setMonth(d.getMonth() - 1);
  else if (preset === '3M') d.setMonth(d.getMonth() - 3);
  else if (preset === '6M') d.setMonth(d.getMonth() - 6);
  else if (preset === '1Y') d.setFullYear(d.getFullYear() - 1);
  else d.setFullYear(d.getFullYear() - 3);
  return d.toISOString().slice(0, 10);
}

/** "0.0512" -> "+5.12%". Returns an em dash for anything unparseable, because
 * printing "NaN%" next to real money numbers destroys trust in all of them. */
function formatPct(v?: string | null): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${(n * 100).toFixed(2)}%`;
}

const METHOD_LABEL: Record<string, string> = {
  time_weighted: 'Time-weighted',
  value_return: 'Value return',
  not_applicable: 'Not applicable',
};

const METHOD_EXPLANATION: Record<string, string> = {
  time_weighted:
    'Each day’s growth chained together with the effect of your deposits and ' +
    'withdrawals removed. It measures how the portfolio performed — not how ' +
    'well-timed your contributions were — which is what makes it fair to ' +
    'compare against a benchmark.',
  value_return:
    'The simple change in value over the window. Daily prices were not complete ' +
    'enough to support a true time-weighted return (see price coverage), so the ' +
    'timing of deposits and withdrawals can distort this figure. It is shown ' +
    'because it is the honest best available, not because it is equivalent.',
  not_applicable:
    'The data in this window cannot support an honest return figure — ' +
    'typically there are no priced holdings, or the account has history the ' +
    'ledger cannot price. Rather than guess, no number is shown.',
};

/**
 * Fixed hue per entity, keyed by symbol rather than selection index, so
 * toggling a benchmark never repaints the survivors. Slots come from the
 * validated categorical palette (light/dark steps of the same hues).
 */
const SERIES_COLORS: Record<string, { light: string; dark: string }> = {
  portfolio: { light: '#2a78d6', dark: '#3987e5' },
  SPY: { light: '#eb6834', dark: '#d95926' },
  'XEQT.TO': { light: '#1baf7a', dark: '#199e70' },
  VTI: { light: '#eda100', dark: '#c98500' },
  'VEQT.TO': { light: '#e87ba4', dark: '#d55181' },
};

export const PerformancePage: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const seriesColor = (key: string) =>
    (SERIES_COLORS[key] ?? SERIES_COLORS.portfolio)[mode];

  const [preset, setPreset] = useState<WindowPreset>('1Y');
  const [benchmarks, setBenchmarks] = useState<string[]>([]);
  const [data, setData] = useState<PerformanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = presetFrom(preset);
      // The portfolio endpoint never carries benchmark overlays; compare does,
      // but with no benchmarks param it returns the backend's default list, so
      // only call it when the user actually picked overlays.
      const resp = benchmarks.length
        ? await PerformanceService.compare({ benchmarks, from })
        : await PerformanceService.getPortfolioPerformance(from);
      setData(resp);
    } catch (e: any) {
      setError(e?.message || 'Failed to load performance');
    } finally {
      setLoading(false);
    }
  }, [preset, benchmarks]);

  useEffect(() => {
    void load();
  }, [load]);

  const notApplicable = data?.method === 'not_applicable';

  /** One row per date carrying the portfolio and every benchmark, so a single
   * chart (one axis, cumulative % for everything) can overlay them. */
  const chartData = useMemo(() => {
    if (!data) return [];
    const byDate = new Map<string, Record<string, string | number>>();
    for (const p of data.series) {
      byDate.set(p.date, { date: p.date, portfolio: Number(p.cumulative_return) });
    }
    for (const b of data.benchmarks ?? []) {
      for (const p of b.series) {
        const row = byDate.get(p.date) ?? { date: p.date };
        row[b.symbol] = Number(p.cumulative_return);
        byDate.set(p.date, row);
      }
    }
    return Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [data]);

  const coveragePct = data ? `${Math.round(data.price_coverage * 100)}%` : null;

  const stats: Array<{ label: string; value: string; hint: string }> = data
    ? [
        {
          label: 'Period return',
          value: notApplicable ? '—' : formatPct(data.return),
          hint: `${data.window.from} → ${data.window.to}`,
        },
        {
          label: 'Annualized',
          value: notApplicable ? '—' : formatPct(data.annualized),
          hint: 'Compound rate per year over the window',
        },
        {
          label: 'XIRR',
          value: notApplicable ? '—' : formatPct(data.xirr),
          hint: 'Money-weighted: what YOUR dollars earned, deposit timing included',
        },
        {
          label: 'Price coverage',
          value: coveragePct ?? '—',
          hint: 'Share of window days every holding could be priced on',
        },
      ]
    : [];

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TimelineIcon color="primary" />
            <Typography variant="h5">Performance</Typography>
            {data && (
              <Tooltip title={METHOD_EXPLANATION[data.method] ?? data.method} arrow>
                <Chip
                  size="small"
                  icon={<InfoIcon />}
                  label={METHOD_LABEL[data.method] ?? data.method}
                  color={
                    data.method === 'time_weighted'
                      ? 'success'
                      : data.method === 'value_return'
                        ? 'warning'
                        : 'default'
                  }
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              label="Benchmarks"
              value={benchmarks}
              onChange={(e) =>
                setBenchmarks(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',').filter(Boolean)
                    : (e.target.value as unknown as string[]),
                )
              }
              // Reason: displayEmpty renders "None" even when nothing is
              // selected, so the label must stay shrunk or the two overlap.
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected: unknown) =>
                  (selected as string[]).length
                    ? (selected as string[]).join(', ')
                    : 'None',
              }}
              sx={{ minWidth: 200 }}
            >
              {SUPPORTED_BENCHMARKS.map((b) => (
                <MenuItem key={b} value={b}>
                  <Box
                    component="span"
                    sx={{
                      width: 10, height: 10, borderRadius: '50%', mr: 1,
                      display: 'inline-block', background: seriesColor(b),
                    }}
                  />
                  {b}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preset}
              onChange={(_, v: WindowPreset | null) => v && setPreset(v)}
            >
              {PRESETS.map((p) => (
                <ToggleButton key={p} value={p} sx={{ px: 1.5 }}>
                  {p}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
          Time-weighted growth of your investment accounts, overlayable against
          benchmarks. Every figure carries the method that produced it &mdash; hover
          the badge above to see what this window&apos;s number does and does not mean.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !data ? null : (
          <>
            {notApplicable && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No return can be computed for this window.{' '}
                {METHOD_EXPLANATION.not_applicable} Try a shorter window that starts
                after your holdings begin, or record the trades (or a holdings
                snapshot) the window is missing.
              </Alert>
            )}
            {data.method === 'value_return' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Daily prices cover only {coveragePct} of this window, so the figures
                below are a simple value return, not a time-weighted one. Deposit
                timing can distort them; treat benchmark comparisons with care.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              {stats.map((s) => (
                <Paper key={s.label} variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160 }}>
                  <Typography variant="overline" color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.hint}
                  </Typography>
                </Paper>
              ))}
            </Stack>

            {!notApplicable && chartData.length > 1 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Cumulative return
                </Typography>
                <Box sx={{ width: '100%', height: 360 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                        minTickGap={48}
                      />
                      <YAxis
                        tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                        width={56}
                      />
                      <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
                      <ChartTooltip
                        {...chartTooltipProps}
                        formatter={(v: any, name: any) => [formatPct(String(v)), name]}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="portfolio"
                        name="Portfolio"
                        stroke={seriesColor('portfolio')}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                      {(data.benchmarks ?? []).map((b) => (
                        <Line
                          key={b.symbol}
                          type="monotone"
                          dataKey={b.symbol}
                          name={b.symbol}
                          stroke={seriesColor(b.symbol)}
                          strokeWidth={2}
                          strokeDasharray="6 3"
                          dot={false}
                          connectNulls
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                {(data.benchmarks ?? []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Benchmark curves are chained from daily closing prices
                    (close-to-close), dividends not reinvested. Returns:{' '}
                    {(data.benchmarks ?? [])
                      .map((b) => `${b.symbol} ${formatPct(b.return)}`)
                      .join(' · ')}
                  </Typography>
                )}
              </Paper>
            )}

            {data.accounts.length > 0 && (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Return</TableCell>
                        <TableCell align="right">Annualized</TableCell>
                        <TableCell align="right">XIRR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.accounts.map((a) => {
                        const na = a.method === 'not_applicable';
                        return (
                          <TableRow key={a.account_id} hover>
                            <TableCell>{a.name}</TableCell>
                            <TableCell>
                              <Tooltip title={METHOD_EXPLANATION[a.method] ?? a.method} arrow>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={METHOD_LABEL[a.method] ?? a.method}
                                  color={
                                    a.method === 'time_weighted'
                                      ? 'success'
                                      : a.method === 'value_return'
                                        ? 'warning'
                                        : 'default'
                                  }
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{na ? '—' : formatPct(a.return)}</TableCell>
                            <TableCell align="right">{na ? '—' : formatPct(a.annualized)}</TableCell>
                            <TableCell align="right">{na ? '—' : formatPct(a.xirr)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </MainLayout>
  );
};
